import React from 'react';

import {Link} from 'react-router';

import {UserAvatar} from 'app/components/general/avatars';
import NoResultsMessage from 'app/components/general/NoResultsMessage';
import {CloseURLSidebarPagePanels} from 'app/general/structures/DocSidebarPagePanels';
import {useObjectPermissions} from 'app/permissions/api';
import {useModelChangeSubscription} from 'app/subscriptions';
import {getUserDetailPageURL} from 'app/urls';
import {gt} from 'app/utils/i18n';

import {ExportPermissionsButton} from './ExportPermissionsButton';

import type {GroupObjectPermissionType} from 'app/permissions/types';
import type {BaseDocumentContentType} from 'sweet-types';

type Props = {
  title: string;
  closeUrl: string;
  contentType: BaseDocumentContentType;
  objectId: number;
  documentUrl: string;
  showExportButton: boolean;
};

function PermissionRows({
  permissions,
}: {
  permissions: Array<GroupObjectPermissionType>;
}) {
  return (
    <ul className="list-unstyled mb-0">
      {permissions.map((permission) => {
        const user = permission.user;
        if (user == null) {
          return null;
        }

        return (
          <li
            key={user.id}
            className="d-flex align-items-center px-3 py-2 border-bottom"
          >
            <UserAvatar className="flex-shrink-0" user={user} small showCard />
            <div className="ml-2 flex-grow-1 overflow-hidden">
              <Link
                className="font-weight-bold small"
                to={getUserDetailPageURL(user.id, 'activity')}
              >
                {user.name || user.email}
              </Link>
              <div className="text-muted small text-truncate">{user.email}</div>
            </div>
            {permission.is_manager ? (
              <span className="ml-2 badge badge-light flex-shrink-0">
                {gt('Manager')}
              </span>
            ) : permission.is_teammate ? (
              <span className="ml-2 badge badge-light flex-shrink-0">
                {gt('Teammate')}
              </span>
            ) : permission.is_reader ? (
              <span className="ml-2 badge badge-light flex-shrink-0">
                {gt('Reader')}
              </span>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

export function DetailPagePermissionsSidebar({
  title,
  closeUrl,
  contentType,
  objectId,
  documentUrl,
  showExportButton,
}: Props) {
  const {currentData, isLoading, refetch} = useObjectPermissions({
    content_type: contentType,
    object_pk: objectId,
  });

  useModelChangeSubscription(
    (message, {runWhenAny, modelIdMatch, anyRelatedMatch}) => {
      runWhenAny(
        message,
        [
          modelIdMatch(contentType, objectId),
          anyRelatedMatch({
            content_type: contentType,
            id: objectId,
          }),
        ],
        () => {
          refetch();
        }
      );
    },
    [contentType, objectId, refetch]
  );

  const permissions = currentData?.results ?? [];

  return (
    <CloseURLSidebarPagePanels
      title={title}
      closeUrl={closeUrl}
      loading={isLoading}
      footer={
        showExportButton ? (
          <ExportPermissionsButton documentUrl={documentUrl} />
        ) : null
      }
    >
      {permissions.length > 0 ? (
        <PermissionRows permissions={permissions} />
      ) : !isLoading ? (
        <NoResultsMessage icon="fa-users" head={gt('No permissions')} onSidebar>
          {gt('No users have been granted access to this document.')}
        </NoResultsMessage>
      ) : null}
    </CloseURLSidebarPagePanels>
  );
}
